import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Gender } from '@/core/types/shared'
import { UpdatePatientDto } from '@/core/models/patient.model'
import { useUpdatePatientMutation } from '@/core/services/patient'
import { MeasurementUnitSystem, useMeasurementUnitSystem } from '@/hooks/useMeasurementUnitSystem'
import { getApiErrorMessage } from '@/utils/errors'
import { parseMeasurementValue, toMeasurementUnit } from '@/utils/unit-system'
import { toUtcMidnight } from '@/utils/dateUtils'

import { PatientProfileFormData } from './usePatientProfileForm'

export function useUpdatePatient({ onUpdate }: { onUpdate?: (dto: UpdatePatientDto) => void }) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { unitSystem } = useMeasurementUnitSystem()

  const [commitMutation, { isLoading }] = useUpdatePatientMutation()

  const updatePatient = async (data: PatientProfileFormData, patientId: string) => {
    const updatePatientDto = prepareUpdatePatientDto(data, unitSystem)

    if (onUpdate) {
      onUpdate(updatePatientDto)
      return
    }

    try {
      await commitMutation({ ...updatePatientDto, id: patientId }).unwrap()
      enqueueSnackbar(t('notification.patient.update.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.patient.update.error')), { variant: 'error' })
    }
  }

  return [updatePatient, isLoading] as const
}

function prepareUpdatePatientDto(data: PatientProfileFormData, unitSystem: MeasurementUnitSystem): UpdatePatientDto {
  return {
    identityConfirmed: data.identityConfirmed,
    agreement: data.agreement,
    contactInfo: {
      phone: data.phone || null,
      street: data.street || null,
      city: data.city || null,
      cp: data.cp === '' ? null : data.cp,
      country: data.country || null,
    },
    userInfo: {
      birthday: toUtcMidnight(data.birthday)?.toISOString() ?? null,
      gender: data.gender ? (data.gender as Gender) : undefined,
      name: data.name,
      birthName: data.birthName,
      birthFirstName: data.birthFirstName,
      surname: data.surname,
      birthSurname: data.birthSurname,
      birthPlaceCode: data.birthPlaceCode,
      identityJustificationType: data.identityJustificationType ?? undefined,
      identitySupplementaryAttribute: data.identitySupplementaryAttribute,
    },
    email: data.email || null,
    nationality: data.nationality || null,
    weight: data.weight && data.weight !== '' ? parseInt(parseMeasurementValue(toMeasurementUnit('kg', unitSystem), data.weight, 'metric'), 10) : null,
    height: data.height && data.height !== '' ? parseInt(parseMeasurementValue(toMeasurementUnit('cm', unitSystem), data.height, 'metric'), 10) : null,
    allergy: data.allergy || null,
    vaccins: data.vaccins || null,
    notes: data.notes || null,
    pluginId: data.pluginId || null,
    netsoinsId: data.pluginId || null,
    background: data.background || null,
    treatment: data.treatment || null,
    parents: _.map(data.parents, (parent) => ({
      firstname: parent.firstname,
      lastname: parent.lastname,
      phone: parent.phone || null,
      email: parent.email || null,
      city: parent.city || null,
      cp: _.isUndefined(parent.cp) || parent.cp === '' ? null : parent.cp,
    })),
    attendingPhysician:
      data.privateDoctor && !!data.privateDoctor.firstname && !!data.privateDoctor.lastname
        ? {
            firstname: data.privateDoctor.firstname,
            lastname: data.privateDoctor.lastname,
            phone: data.privateDoctor.phone || null,
            email: data.privateDoctor.email || null,
            city: data.privateDoctor.city || null,
            cp: _.isUndefined(data.privateDoctor.cp) || data.privateDoctor.cp === '' ? null : data.privateDoctor.cp,
          }
        : null,
    insNIR: data.insNIR || null,
    insOID: data.insOID || null,
    insEmitter: data.insEmitter || null,
    insured: data.insured || null,
    insuredRelationship: data.insuredRelationship || null,
    insuranceProvider: data.insuranceProvider || null,
    primaryInsurance: data.primaryInsurance || null,
    secondaryInsurance: data.secondaryInsurance || null,
    primaryPolicy: data.primaryPolicy || null,
    secondaryPolicy: data.secondaryPolicy || null,
    primaryPharmacy: data.primaryPharmacy || null,
  }
}
